import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { receiptDocumentPath } from '../../../api/receipt.api';
import { DONATION_TONE } from '../../../constants/donation.const';
import { useDownloadInFlight } from '../../../hooks/useDownloadInFlight';
import type { DonationLine } from '../../../types/donation.types';
import { formatCurrency } from '../../../utils/fiat-currency.util';

interface DonationLineRowProps {
  donation: DonationLine;
  primary: string;
  secondary: string;
  onDownloadReceipt: (donation: DonationLine) => void;
}

const SUMMARY_CLASS =
  'flex min-w-0 flex-1 items-center justify-between gap-3 text-left';

/**
 * One donation in a list: opens the checkout it was made in, with its receipt beside it. A
 * donation with no checkout to open is shown, not linked.
 */
export function DonationLineRow({
  donation,
  primary,
  secondary,
  onDownloadReceipt
}: DonationLineRowProps) {
  const navigate = useNavigate();
  const preparing = useDownloadInFlight(
    receiptDocumentPath.donation(donation.donation_ref)
  );
  const activityId = donation.order_activity_id;

  const summary = (
    <>
      <span className="min-w-0">
        <span className="block truncate text-sm text-gray-900">{primary}</span>
        <span className="block truncate text-xs text-gray-500">
          {secondary}
        </span>
      </span>
      <span className={clsx('shrink-0 text-sm font-bold', DONATION_TONE.text)}>
        {formatCurrency(donation.amount, donation.currency)}
      </span>
    </>
  );

  return (
    <li className="flex items-center gap-2 py-3">
      {activityId ? (
        <button
          type="button"
          onClick={() =>
            navigate(`/transactions/${encodeURIComponent(activityId)}`)
          }
          className={SUMMARY_CLASS}
        >
          {summary}
        </button>
      ) : (
        <div className={SUMMARY_CLASS}>{summary}</div>
      )}
      <button
        type="button"
        onClick={() => onDownloadReceipt(donation)}
        disabled={preparing}
        aria-busy={preparing}
        aria-label={
          preparing
            ? `Preparing receipt ${donation.donation_ref}`
            : `Download receipt ${donation.donation_ref}`
        }
        className={clsx(
          'shrink-0 rounded-md p-1.5 transition-colors hover:bg-[#f0faf5] disabled:hover:bg-transparent',
          DONATION_TONE.text
        )}
      >
        {preparing ? (
          <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
        ) : (
          <ArrowDownTrayIcon className="h-5 w-5" aria-hidden="true" />
        )}
      </button>
    </li>
  );
}
